import { Search, Settings2, Sun, Moon, User as UserIcon, LogOut, Pencil, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTheme } from "@/components/dashboard/ThemeProvider";
import { useAuth } from "@/lib/auth-context";
import { CommandPalette } from "@/components/dashboard/CommandPalette";
import { NotificationsPopover } from "@/components/dashboard/NotificationsPopover";
import { useDashboardLayout } from "@/lib/dashboard-layout";
import { cn } from "@/lib/utils";
import { apiRequest, readUISettings } from "@/lib/api";
import { daemonAccountDisplayName, fetchDaemonAccount } from "@/lib/daemon-account";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Topbar({ title, subtitle }: { title: string; subtitle?: string }) {
  const [time, setTime] = useState<string>("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [daemonBaseURL, setDaemonBaseURL] = useState("");
  const [daemonNodeID, setDaemonNodeID] = useState("");
  const [daemonAccountName, setDaemonAccountName] = useState("");
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { editing, setEditing } = useDashboardLayout();
  const isDashboard = pathname === "/";

  useEffect(() => {
    const tick = () =>
      setTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isWalletCloudMode()) {
      setDaemonBaseURL("");
      setDaemonNodeID("");
      setDaemonAccountName("Hosted wallet");
      return () => {
        cancelled = true;
      };
    }
    const activeUserID = String(user?.id || "").trim();
    const scopedSettings = readUISettings(activeUserID || undefined);
    const base = String(scopedSettings.hub_base || "").trim();
    setDaemonBaseURL(base);
    setDaemonNodeID("");
    setDaemonAccountName("");
    if (!base) return () => { cancelled = true; };

    void apiRequest<{ hub_id?: string }>(
      "/api/v1/runtime/health",
      { method: "GET", timeoutMs: 6000 },
      scopedSettings,
    ).then((payload) => {
      if (cancelled) return;
      setDaemonNodeID(String(payload?.hub_id || "").trim());
    }).catch(() => {
      if (cancelled) return;
      setDaemonNodeID("");
    });

    void fetchDaemonAccount(base, 6000).then((account) => {
      if (cancelled) return;
      setDaemonAccountName(daemonAccountDisplayName(account));
    }).catch(() => {
      if (cancelled) return;
      setDaemonAccountName("");
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const displayName = user?.name ?? "Operator";
  const initials = displayName.slice(0, 2).toUpperCase();
  const daemonIdentity = daemonAccountName || daemonNodeID;
  const daemonSummary = isWalletCloudMode()
    ? "Hosted wallet · browser-native mode"
    : daemonBaseURL
    ? `${daemonBaseURL}${daemonIdentity ? ` · ${daemonIdentity}` : ""}`
    : "No daemon linked";
  return (
    <>
      <header className="sticky top-0 z-20 flex items-center gap-4 border-b hairline bg-[var(--background)]/60 px-6 py-4 backdrop-blur-2xl">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="hidden items-center gap-2 rounded-full border hairline bg-soft px-3.5 py-2 text-xs text-muted-foreground transition hover:text-foreground lg:flex"
        >
          <Search className="h-3.5 w-3.5" />
          <span>Search wallets, balances…</span>
          <kbd className="ml-2 rounded bg-soft px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
        </button>
        <div className="flex items-center gap-2">
          {isDashboard && (
            <button
              onClick={() => setEditing(!editing)}
              aria-label={editing ? "Done editing" : "Edit dashboard"}
              title={editing ? "Done editing" : "Edit dashboard"}
              className={cn(
                "flex items-center gap-1.5 rounded-xl border hairline px-2.5 py-2 text-xs font-medium transition",
                editing
                  ? "border-transparent text-primary-foreground shadow-[var(--shadow-glow)]"
                  : "bg-soft text-muted-foreground hover:text-foreground",
              )}
              style={editing ? { background: "var(--gradient-primary)" } : undefined}
            >
              {editing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{editing ? "Done" : "Edit"}</span>
            </button>
          )}
          <button onClick={toggle} aria-label="Toggle theme" className="rounded-xl border hairline bg-soft p-2 text-muted-foreground transition hover:text-foreground">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <NotificationsPopover />
          <button
            onClick={() => navigate({ to: "/settings" })}
            aria-label="Settings"
            className="rounded-xl border hairline bg-soft p-2 text-muted-foreground transition hover:text-foreground"
          >
            <Settings2 className="h-4 w-4" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-1 flex items-center gap-2 rounded-xl border hairline bg-soft px-3 py-1.5 transition hover:text-foreground">
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold text-primary-foreground"
                  style={{ background: "var(--gradient-primary)" }}
                >
                  {initials}
                </div>
                <div className="hidden text-left lg:block">
                  <p className="text-xs font-medium leading-none">{displayName}</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground" suppressHydrationWarning>
                    {time || "—"} · synced
                  </p>
                  <p className="mt-0.5 max-w-[240px] truncate font-mono text-[10px] text-muted-foreground" title={daemonSummary}>
                    {daemonSummary}
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex flex-col">
                <span className="text-xs font-semibold">{displayName}</span>
                <span className="text-[10px] font-normal text-muted-foreground">{user?.email ?? "—"}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate({ to: "/settings" })}>
                <UserIcon className="mr-2 h-4 w-4" /> Profile & settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggle}>
                {theme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                Toggle {theme === "dark" ? "light" : "dark"} mode
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  if (!isWalletCloudMode()) {
                    navigate({ to: "/login" });
                  }
                }}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
